import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragHandle, DragOutcome } from './buildSchedulePatch';
import {
  DRAG_THRESHOLD_PX,
  clampMoveDelta,
  snapMinutes,
  xPxToMinutes,
  type MinuteRange,
} from '../../../shared/lib/schedule';

/**
 * The board's one drag primitive — move and resize, every block kind, one implementation.
 *
 * `apps/web/CLAUDE.md` calls out the deleted timeline's three parallel drag implementations as the
 * thing not to rebuild. Per-kind differences live in `dragPolicy.ts`; this file only turns pointer
 * movement into a snapped minute delta and a target row.
 *
 * Generic over the dragged subject: the hook itself only ever reads a `key` and a `span`, so real
 * blocks and the planning draft ride the same instance (`dragSubject.ts`) instead of a second
 * implementation.
 */

/** The least a drag subject must expose: identity for the ghost, span for the move clamp. */
export interface DragItem {
  key: string;
  span: MinuteRange;
}

/** Rows carry this so the pointer can be mapped back to a technician mid-drag. */
export const ROW_TECHNICIAN_ATTR = 'data-technician-id';

export interface DragState {
  blockKey: string;
  handle: DragHandle;
  /** Snapped, and clamped so a move cannot leave the drawn window. */
  deltaMinutes: number;
  /** Row under the pointer right now. */
  technicianId: number;
  originTechnicianId: number;
}

interface DragRef<T extends DragItem> {
  item: T;
  handle: DragHandle;
  originTechnicianId: number;
  canReassign: boolean;
  startX: number;
  startY: number;
  moved: boolean;
}

function technicianUnderPointer(x: number, y: number): number | null {
  const el = document.elementFromPoint(x, y);
  const row = el?.closest(`[${ROW_TECHNICIAN_ATTR}]`);
  const raw = row?.getAttribute(ROW_TECHNICIAN_ATTR);
  if (raw == null) return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

export function useBlockDrag<T extends DragItem>(
  onCommit: (item: T, outcome: DragOutcome) => void,
) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const ref = useRef<DragRef<T> | null>(null);
  // Mirrors `drag` for the window listeners below. They are attached once, so their closure would
  // otherwise only ever see the initial `null` — which is why the commit used to read the state
  // from inside a `setDrag` updater. It must not: React runs updaters during the render phase and
  // re-runs them whenever a render attempt is restarted, and the commit fires a mutation whose
  // store notification restarts that very render. One drop turned into ~50 identical requests.
  const stateRef = useRef<DragState | null>(null);
  // A real drag ends with a `click` too. This lets the block skip opening its modal.
  const draggedRef = useRef(false);
  // Read inside the window listeners, which are attached once and must not capture a stale callback.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const startDrag = useCallback(
    (
      item: T,
      handle: DragHandle,
      canReassign: boolean,
      technicianId: number,
      e: React.PointerEvent,
    ) => {
      // Left button only, and never let the browser start a text selection or image drag.
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      draggedRef.current = false;
      stateRef.current = null;
      ref.current = {
        item,
        handle,
        originTechnicianId: technicianId,
        canReassign,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
      };
    },
    [],
  );

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const current = ref.current;
      if (!current) return;

      const dx = e.clientX - current.startX;
      const dy = e.clientY - current.startY;

      // Below the threshold this is still a click — the block's onClick must survive.
      if (!current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      current.moved = true;

      const raw = snapMinutes(xPxToMinutes(dx));
      // Only a whole-block move is bounded by the window; a resize is bounded by its own minimums,
      // which `buildSchedulePatch` applies.
      const deltaMinutes =
        current.handle === 'move' ? clampMoveDelta(raw, current.item.span) : raw;

      const hovered = current.canReassign ? technicianUnderPointer(e.clientX, e.clientY) : null;

      const next: DragState = {
        blockKey: current.item.key,
        handle: current.handle,
        deltaMinutes,
        technicianId: hovered ?? current.originTechnicianId,
        originTechnicianId: current.originTechnicianId,
      };

      stateRef.current = next;
      setDrag(next);
    };

    const onPointerUp = () => {
      const current = ref.current;
      const state = stateRef.current;
      ref.current = null;
      stateRef.current = null;

      setDrag(null);

      // After the state write, never inside it: this sends a request, and a side effect in a
      // `setState` updater is replayed on every discarded render attempt. Both calls sit in the
      // same event handler, so React still batches them into one render.
      if (!current?.moved || !state) return;

      draggedRef.current = true;
      commitRef.current(current.item, {
        handle: state.handle,
        deltaMinutes: state.deltaMinutes,
        technicianId: state.technicianId,
      });
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  /** True when the gesture that just ended was a drag, so the trailing `click` can be ignored. */
  const wasDragged = useCallback(() => draggedRef.current, []);

  return { drag, startDrag, wasDragged };
}
